/**
 * Format a date string to display in local format without timezone conversion
 * @param {string} dateString - ISO date string from backend
 * @returns {string} Formatted date string
 */
export const formatDateForDisplay = (dateString) => {
  if (!dateString) return '';

  // Extract just the date part (YYYY-MM-DD) to avoid timezone issues
  const datePart = dateString.split('T')[0];
  const [year, month, day] = datePart.split('-');

  // Create date using local timezone
  const date = new Date(year, month - 1, day);

  return date.toLocaleDateString();
};

/**
 * Format a date object or string to a short date format (e.g., "Jan 15, 2024")
 * @param {Date|string} date - Date object or ISO string
 * @returns {string} Formatted date string or 'N/A' if no date
 */
export const formatDate = (date) => {
  if (!date) return 'N/A';

  const dateObj = date instanceof Date ? date : new Date(date);

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC'
  }).format(dateObj);
};

const PROGRAM_TIME_ZONE = 'America/New_York';

// Enrollment deadlines are business dates in MVYP's Eastern timezone. Format
// them in that timezone rather than UTC, where an end-of-day Eastern cutoff
// falls on the following calendar day.
export const formatEnrollmentDeadline = (date) => {
  if (!date) return 'N/A';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', timeZone: PROGRAM_TIME_ZONE
  }).format(new Date(date));
};

// Convert a stored deadline instant back to the Eastern calendar date expected
// by an <input type="date">.
export const enrollmentDeadlineInputValue = (date) => {
  if (!date) return '';
  const parts = new Intl.DateTimeFormat('en-US', {
    year: 'numeric', month: '2-digit', day: '2-digit', timeZone: PROGRAM_TIME_ZONE
  }).formatToParts(new Date(date));
  const get = (type) => parts.find((p) => p.type === type)?.value;
  return `${get('year')}-${get('month')}-${get('day')}`;
};

// Store a selected deadline date as 11:59:59 PM Eastern. Determine the UTC
// offset using that date so daylight-saving changes are handled correctly.
export const enrollmentDeadlineToISOString = (dateString) => {
  if (!dateString) return null;
  const [year, month, day] = dateString.split('-').map(Number);
  const probe = new Date(Date.UTC(year, month - 1, day, 12));
  const zoneName = new Intl.DateTimeFormat('en-US', {
    timeZone: PROGRAM_TIME_ZONE, timeZoneName: 'longOffset'
  }).formatToParts(probe).find((p) => p.type === 'timeZoneName')?.value || 'GMT-05:00';
  const match = zoneName.match(/GMT([+-])(\d{2}):(\d{2})/);
  const offsetMinutes = match
    ? (match[1] === '+' ? 1 : -1) * (Number(match[2]) * 60 + Number(match[3]))
    : -300;
  const endOfDayUtc = Date.UTC(year, month - 1, day + 1) - offsetMinutes * 60000 - 1000;
  return new Date(endOfDayUtc).toISOString();
};

/**
 * Format a date/time string to include time (e.g., "Jan 15, 2024, 2:30 PM")
 * @param {string} dateString - ISO date/time string
 * @returns {string} Formatted date/time string
 */
export const formatDateTime = (dateString) => {
  if (!dateString) return '';

  const date = new Date(dateString);

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  }).format(date);
};

/**
 * Calculate age from date of birth
 * @param {string} dateOfBirth - ISO date string
 * @returns {number} Age in years
 */
export const calculateAge = (dateOfBirth) => {
  if (!dateOfBirth) return null;

  const datePart = dateOfBirth.split('T')[0];
  const [year, month, day] = datePart.split('-');

  const birthDate = new Date(year, month - 1, day);
  const today = new Date();

  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();

  // Adjust if birthday hasn't occurred yet this year
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }

  return age;
};
